import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { getQueryString } from '../../helpers/url';
import { WelletContext } from '../../context/WelletContext';
import { useDispatch, useSelector } from 'react-redux';
import { errorText } from '../../helpers/login';
import { useTranslation } from 'react-i18next';
import { setCanRefresh } from '../../actions/appActions';
import Header from '../../components/Common/Header/Header';
import ContentApp from '../../components/ContentApp';
import Button from '../../components/Common/Button/Button';
import useForm from '../../helpers/useForm';
import ErrorBox from '../../components/Common/ErrorBox/ErrorBox';
import { Spinner } from '../../components/Common/Spinner/Spinner';
import InputFormBorder from '../../components/Common/InputFormBorder/InputFormBorder';
import RPAndCitySelector from '../../components/Common/RpAndCitySelector/RpAndCitySelector';
import GeneralSelectorPicker from '../../components/SelectorPicker/GeneralSelectorPicker';
import SolidChevron from '../../components/icons/SolidChevron';

const SignUpEmailValidationScreenRosanegraSimple = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const phone = getQueryString('phone');
  const token = getQueryString('token');
  const countryCode = getQueryString('countryCode');
  const welletContext = useContext(WelletContext);
  const tenantId = useSelector((state) => state.app.tenantId);

  const requiresRPParam = getQueryString('requiresRP') === 'true';
  const requiresRP =
    useSelector((state) => state.app?.currentMarket?.requiresRP) ?? requiresRPParam;

  const dispatch = useDispatch();

  const [verifyingUser, setVerifyingUser] = useState(false);
  const [errorUser, setErrorUser] = useState(undefined);

  const [selectedRp, setSelectedRp] = useState({ label: t('signUp.selectRp') });
  const [selectedCity, setSelectedCity] = useState({ label: t('signUp.selectCity') });

  const [affiliateType, setAffiliateType] = useState(null);
  const [subAffiliateType, setSubAffiliateType] = useState(null);
  const [otherIndependent, setOtherIndependent] = useState('');

  useEffect(() => {
    dispatch(setCanRefresh(false));
  }, [dispatch]);

  function validate(values) {
    const errors = {};

    if (!values.email) {
      errors.email = t('emailError.EMPTY_EMAIL');
    } else if (!values.email.includes('@')) {
      errors.email = t('emailError.EMAIL_@');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
      errors.email = t('emailError.INVALID_EMAIL');
    }

    if (!values.emailConfirmation) {
      errors.emailConfirmation = t('emailError.EMPTY_EMAIL');
    } else if (values.email !== values.emailConfirmation) {
      errors.emailConfirmation = t('emailError.EMAIL_NOT_MATCH');
    }

    if (!affiliateType) {
      errors.affiliateType = t('affiliateType.error.selectAffiliateType');
    }

    if (!values.firstName) errors.firstName = t('nameError.EMPTY_NAME');
    if (!values.lastName) errors.lastName = t('nameError.EMPTY_LAST_NAME');

    if (affiliateType === 'CONCIERGE') {
      if (!values.hotelName) errors.hotelName = t('affiliateType.error.emptyHotelName');
      if (!values.city) errors.city = t('selectCity');
      if (requiresRP && !values.rp) {
        errors.rp = t('selectRP');
      }
    }

    if (affiliateType === 'INDEPENDENT') {
      if (!subAffiliateType) {
        errors.subAffiliateType = t('affiliateType.error.selectIndependentAffiliateType');
      } else if (subAffiliateType === t('affiliateType.options.other') && !otherIndependent) {
        errors.otherIndependent = t('Por favor especificá tu actividad');
      }
    }

    return errors;
  }

  const sendForm = async () => {
    setVerifyingUser(true);
    try {
      const result = await welletContext.apis.tickets.post(`users/Available`, {
        email: values.email,
        tenantId,
      });

      if (result.data.isAvailable) {
        errorText(result.data.availableMessage, setErrorUser, t);
      } else {
        let query = `?phone=${phone}&countryCode=${countryCode}&email=${encodeURIComponent(
          values.email,
        )}&token=${token}`;
        query += `&firstName=${encodeURIComponent(values.firstName)}&lastName=${encodeURIComponent(
          values.lastName,
        )}`;
        query += `&affiliateType=${encodeURIComponent(affiliateType)}`;

        if (subAffiliateType) {
          const value =
            subAffiliateType === t('affiliateType.options.other')
              ? otherIndependent
              : subAffiliateType;

          query += `&sectorAffiliate=${encodeURIComponent(value)}`;
        }

        if (affiliateType === 'CONCIERGE' && values.hotelName) {
          query += `&hotelName=${encodeURIComponent(values.hotelName)}`;
        }

        if (affiliateType === 'CONCIERGE') {
          query += `&city=${values.city || ''}`;
          if (requiresRP) query += `&rp=${values.rp || ''}`;
        }

        query += `&requiresRP=${requiresRP}`;

        history.push({
          pathname: '/sign-up-selfie',
          search: query,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setVerifyingUser(false);
    }
  };

  const onChange = (e) => {
    setErrorUser('');
    handleChange(e);
  };

  const { values, errors, handleChange, handleSubmit } = useForm(sendForm, validate);

  const DropdownIndicator = () => <SolidChevron size={18} color='var(--color-primary)' />;

  const selectorStyles = {
    control: (provided) => ({
      ...provided,
      paddingLeft: '.4rem',
      boxShadow: 'none',
      backgroundColor: '#000',
      border: 'none',
      borderRadius: '10px',
      height: '61.38px',
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      color: '#000',
    }),
  };

  const affiliateOptions = [
    { value: 'CONCIERGE', label: t('affiliateType.options.CONCIERGE') },
    { value: 'INDEPENDENT', label: t('affiliateType.options.INDEPENDENT') },
  ];

  const optionKeys = [
    'driverUber',
    'driverDidi',
    'driverTaxi',
    'influencer',
    'contentCreator',
    'youtuber',
    'executiveSecretary',
    'tourPhotographer',
    'receptionist',
    'weddingPlanner',
    'eventOrganizer',
    'tourGuide',
    'independentTourism',
    'hostAirbnb',
    'accommodationOwner',
    'freelancer',
    'other',
  ];

  const independentOptions = optionKeys.map((key) => {
    const text = t(`affiliateType.options.${key}`);
    return { value: text, label: text };
  });

  return (
    <>
      <Header />
      <ContentApp background={true}>
        <div>
          <div className='font-medium bold text-white' style={{ lineHeight: '1.6rem' }}>
            {t('yourPersonalInformation')}
          </div>
          <div className='mt-2 text-secondary subtitle' style={{ lineHeight: '1.3rem' }}>
            {t('signUp.subtitle3')}
          </div>

          <form onSubmit={handleSubmit}>
            <div className='mt-4'>
              <InputFormBorder
                name='firstName'
                type='text'
                value={values.firstName || ''}
                error={errors.firstName}
                label={t('profile.firstName')}
                placeholder={t('profile.firstName')}
                onChange={onChange}
              />
            </div>

            <div className='mt-4'>
              <InputFormBorder
                name='lastName'
                type='text'
                value={values.lastName || ''}
                error={errors.lastName}
                label={t('profile.lastName')}
                placeholder={t('profile.lastName')}
                onChange={onChange}
              />
            </div>

            <div className='mt-4 font-normal'>
              <InputFormBorder
                name='email'
                type='email'
                value={values.email || ''}
                error={errors.email}
                label={t('E-mail')}
                placeholder={t('enterEmail')}
                onChange={onChange}
                forceLowecase
              />
            </div>

            <div className='mt-4'>
              <InputFormBorder
                name='emailConfirmation'
                type='email'
                value={values.emailConfirmation || ''}
                error={errors.emailConfirmation}
                label={t('signUp.confirmEmail')}
                placeholder={t('reEnterEmail')}
                onChange={onChange}
                forceLowecase
              />
            </div>

            <div className={`field-border-container mt-4 ${errors.affiliateType ? 'error' : ''}`}>
              <div className='field-none-border p-0'>
                <div className='field-border-name' style={{ zIndex: '2' }}>
                  {t('affiliateType.label.affiliateType')}
                </div>
                <GeneralSelectorPicker
                  className='w-100 mt-0 hide-indicator-separator font-normal'
                  styles={selectorStyles}
                  isSearchable={false}
                  options={affiliateOptions}
                  onChange={(option) => {
                    setAffiliateType(option.value);
                    setSubAffiliateType(null);
                    setOtherIndependent('');
                  }}
                  placeholder={t('selectType')}
                  components={{ DropdownIndicator }}
                  value={affiliateOptions.find((o) => o.value === affiliateType) || null}
                />
              </div>
              {errors.affiliateType && <div className='errorLabel'>{errors.affiliateType}</div>}
            </div>

            {affiliateType === 'CONCIERGE' && (
              <div className='mt-4'>
                <RPAndCitySelector
                  requiresRP={requiresRP}
                  selectedRp={selectedRp}
                  setSelectedRp={setSelectedRp}
                  selectedCity={selectedCity}
                  setSelectedCity={setSelectedCity}
                  handleChange={onChange}
                  errors={errors}
                  values={values}
                />
              </div>
            )}

            {affiliateType === 'INDEPENDENT' && (
              <div
                className={`field-border-container mt-4 ${errors.subAffiliateType ? 'error' : ''}`}
              >
                <div className='field-none-border p-0'>
                  <div className='field-border-name' style={{ zIndex: '4' }}>
                    {t('affiliateType.label.independentAffiliateType')}
                  </div>
                  <GeneralSelectorPicker
                    className='w-100 mt-0 hide-indicator-separator font-normal'
                    styles={selectorStyles}
                    isSearchable={false}
                    options={independentOptions}
                    onChange={(option) => {
                      setSubAffiliateType(option.value);
                      if (option.value !== t('affiliateType.options.other')) {
                        setOtherIndependent('');
                      }
                    }}
                    placeholder={t('selectType')}
                    components={{ DropdownIndicator }}
                    value={independentOptions.find((o) => o.value === subAffiliateType) || null}
                  />
                </div>
                {errors.subAffiliateType && (
                  <div className='errorLabel'>{errors.subAffiliateType}</div>
                )}
              </div>
            )}
            {subAffiliateType === t('affiliateType.options.other') && (
              <div className='mt-4'>
                <InputFormBorder
                  name='otherIndependent'
                  type='text'
                  value={otherIndependent}
                  error={errors.otherIndependent}
                  label={t('Especifica tu actividad')}
                  placeholder={t('Describe tu actividad')}
                  onChange={(e) => setOtherIndependent(e.target.value)}
                />
              </div>
            )}
            {errorUser && (
              <div className='mt-5'>
                <ErrorBox title={errorUser} />
              </div>
            )}

            <div className='mt-4 font-tiny text-secondary'>{t('signUp.confirmText')}</div>

            <div className='mt-4'>
              <Button
                size='large'
                fontSize='18px'
                text={
                  verifyingUser ? (
                    <div className='d-flex justify-content-center'>
                      <Spinner size={25} color='#000' />
                    </div>
                  ) : (
                    t('continue')
                  )
                }
                icon={!verifyingUser}
                typeIcon='arrow'
                iconColor='#000'
              />
            </div>
          </form>
        </div>
      </ContentApp>
    </>
  );
};

export default SignUpEmailValidationScreenRosanegraSimple;
